import { Alert, Box, Button, Divider, Link, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { Navigate } from "react-router";
import { VibeTesting } from "../../components/vibe-qa/vibe-it";
import { APIKeyProvider, useAPIKeyContext } from "../../contexts/api-key-context";
import { useApiMutation, useApiQuery, useClerkToken } from "../../hooks";
import { BaseLayout } from "../../layouts/base";
import type { SuccessResponse } from "../../types/api";

type RegisterRequest = {
  email: string;
  source: "vibe-qa";
};

export const PublicVibeQAInner = () => {
  const { apiKey, setApiKey, hasKey } = useAPIKeyContext();
  const [email, setEmail] = React.useState("");

  const {
    mutate: registerEmail,
    isPending: isRegistering,
    isSuccess: isRegistered,
    error: registerError,
    reset: resetRegister,
  } = useApiMutation<RegisterRequest, SuccessResponse>("POST", "/vibe-qa/register");

  const { data, error } = useApiQuery<SuccessResponse>(
    "/vibe-qa/check",
    {
      enabled: !!apiKey,
      retry: false,
    },
    apiKey,
  );

  if (hasKey && data?.status === "success") {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
        }}
      >
        <VibeTesting />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack spacing={2} maxWidth={600} sx={{ width: "100%", height: "100%", flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <img
            src="/logo-blue.png"
            alt="Vibe QA Logo"
            style={{ width: "auto", height: 48, alignSelf: "center" }}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h5" gutterBottom>
          Vibe QA Setup
        </Typography>
        <Typography variant="body1" gutterBottom>
          To get started you will need to register at{" "}
          <Link href="https://app.desplega.ai" target="_blank" rel="noopener">
            desplega.ai
          </Link>
          , let us know your email and we will send you a free API key.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            type="email"
            value={email}
            disabled={isRegistering || isRegistered}
            onChange={(e) => {
              setEmail(e.target.value);
              if (registerError || isRegistered) resetRegister();
            }}
            placeholder="We will send you a free API key!"
          />
          {email && email.length > 0 && email.includes("@") && !isRegistered && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isRegistering}
              onClick={() => registerEmail({ email, source: "vibe-qa" })}
              fullWidth
              sx={{ maxWidth: "120px" }}
            >
              {isRegistering ? "Sending…" : "Register"}
            </Button>
          )}
        </Stack>
        {isRegistered && (
          <Alert severity="success">
            Check your inbox — we just emailed an invite with your free API key to {email}.
          </Alert>
        )}
        {registerError && (
          <Alert severity="error" onClose={() => resetRegister()}>
            {registerError.message || "Could not register. Please try again."}
          </Alert>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          If you already have an account, just enter your API key to continue.
        </Typography>
        <TextField
          label="API Key"
          variant="outlined"
          fullWidth
          type="password"
          value={apiKey || ""}
          autoComplete="new-password"
          onChange={(e) => setApiKey(e.target.value)}
          error={!!error}
          helperText={
            error
              ? "Invalid API key or request failed."
              : data?.message || "Enter your API key to test the public endpoint."
          }
          placeholder="Enter your API key to test the public endpoint"
        />
      </Stack>
    </Box>
  );
};

export const PublicVibeQA = () => {
  const { token } = useClerkToken();
  const qs = new URLSearchParams(window.location.search);

  if (token) {
    qs.delete("apiKey");
    return (
      <Navigate
        to={{
          pathname: "/vibe-qa",
          search: qs.toString(),
        }}
      />
    );
  }

  return (
    <APIKeyProvider loggedIn={false}>
      <BaseLayout>
        <PublicVibeQAInner />
      </BaseLayout>
    </APIKeyProvider>
  );
};
